import { AsyncPipe } from '@angular/common';
import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	effect,
	HostBinding,
	inject,
	input,
	output,
	signal,
} from '@angular/core';
import {
	FormControl,
	FormGroup,
	FormsModule,
	ReactiveFormsModule,
} from '@angular/forms';
import { TranslocoPipe } from '@jsverse/transloco';
import { AlertService } from '@marmon/frontend/alert';
import {
	applyValidators,
	copyToClipboard,
	DestroyService,
	Preference,
	RelativeTimePipe,
} from '@marmon/frontend/common';
import {
	MultiselectSearchComponent,
	SelectSearchOption,
} from '@marmon/frontend/components';
import {
	Analysis,
	CalculationType,
	DefaultCalculationType,
} from '@marmon/shared/types/analysis';
import { TuiAnimated } from '@taiga-ui/cdk';
import {
	TuiButton,
	TuiDataList,
	TuiDropdown,
	TuiIcon,
	TuiLoader,
} from '@taiga-ui/core';
import { TuiChevron } from '@taiga-ui/kit';
import { map, takeUntil, timer } from 'rxjs';
import { LoadingCalculation } from '../../services';

const FinancialOptions: {
	[key in CalculationType]: SelectSearchOption<CalculationType>;
} = {
	revenue: {
		label: 'analysis.filters.financial-revenue',
		text: 'generic.revenue',
		value: 'revenue',
	},
	margin: {
		label: 'analysis.filters.financial-margin',
		text: 'generic.margin',
		value: 'margin',
	},
};

export type AnalysisFilterFormValue = {
	financial: SelectSearchOption<CalculationType>;
	segments: string[];
};

@Component({
	standalone: true,
	imports: [
		TuiAnimated,
		TuiButton,
		TuiDataList,
		TuiDropdown,
		TuiIcon,
		TranslocoPipe,
		TuiChevron,
		MultiselectSearchComponent,
		FormsModule,
		ReactiveFormsModule,
		TuiLoader,
		RelativeTimePipe,
		AsyncPipe,
	],
	changeDetection: ChangeDetectionStrategy.OnPush,
	selector: 'mar-analysis-filters',
	templateUrl: 'analysis-filters.component.html',
	host: { class: 'flex w-full flex-col' },
	styles: [
		`
			form.tui-enter,
			form.tui-leave {
				animation-name: tuiFade, tuiCollapse;
			}
		`,
	],
	providers: [DestroyService, RelativeTimePipe],
})
export class AnalysisFiltersComponent extends applyValidators(Component) {
	readonly destroy$ = inject(DestroyService);
	readonly cdRef = inject(ChangeDetectorRef);
	readonly relativeTime = inject(RelativeTimePipe);
	readonly alerts = inject(AlertService);

	readonly analysis = input.required<Analysis>();
	readonly calculationType = input.required<CalculationType>();
	readonly segments = input.required<string[]>();

	readonly tutorialComplete = new Preference(
		'tutorial.calculations.location',
	);

	readonly filterChange = output<AnalysisFilterFormValue>();
	readonly clear = output<void>();
	readonly loadChange = output<void>();

	readonly loadingCalculation = input<LoadingCalculation | null>(null);
	readonly loadingTime$ = timer(0, 1000).pipe(
		map(() => this.loadingCalculation()?.startedAt),
		map((value) => {
			if (!value) return null;
			return this.relativeTime.transform(value, {
				showNow: false,
				exact: true,
				ago: false,
			});
		}),
	);

	/**
	 * @todo make this preference be saved to the user's session and adhere to
	 * that setting when it renders.
	 */
	@HostBinding('attr.open')
	readonly _open = new Preference('analysis.filter.open');
	get open() {
		return this._open.valueOf();
	}

	readonly financialOptions = Object.values(FinancialOptions);

	lastValue = signal<AnalysisFilterFormValue | null>(null);
	valid = signal<boolean>(false);

	form: FormGroup | null = null;

	constructor() {
		super({});

		effect(() => {
			this.setupForm(this.segments(), this.calculationType());
		});

		effect(() => {
			const form = this.form;
			if (!form) return;

			const loading = this.loadingCalculation();
			if (loading && form.enabled) {
				form.disable({ emitEvent: false });
			} else if (!loading && form.disabled) {
				form.enable({ emitEvent: false });
			}
		});
	}

	determineValidity(value: AnalysisFilterFormValue) {
		if (!this.form) return false;
		if (this.form.invalid) return false;
		if (this.form.pristine) return false;

		const formValue = value ?? (this.form.value as AnalysisFilterFormValue);
		const lastValue = this.lastValue();

		// we do not want to allow an update unless things have changed
		return JSON.stringify(formValue) !== JSON.stringify(lastValue);
	}

	setupForm(segments: string[], calculationType: CalculationType) {
		this.form = new FormGroup({
			financial: new FormControl(FinancialOptions[calculationType]),
			segments: new FormControl(segments, (field) =>
				this.getMinLengthValidator(field),
			),
		});

		this.form.valueChanges
			.pipe(takeUntil(this.destroy$))
			.subscribe((value: AnalysisFilterFormValue) => {
				this.valid.set(this.determineValidity(value));
			});

		this.lastValue.set(this.form.getRawValue());
	}

	restoreDefaultValues() {
		if (!this.form)
			throw Error('A form and analysis are required to restore default');

		this.form.patchValue({
			financial: FinancialOptions[DefaultCalculationType],
			segments: this.analysis().segments,
		});
	}

	onOpenToggle() {
		this._open.set(!this._open.valueOf());
	}

	onCopyLink() {
		return copyToClipboard(window.location.href)
			.then(() =>
				this.alerts.open('analysis-link-copied-success').subscribe(),
			)
			.catch(() =>
				this.alerts.open('analysis-link-copied-error').subscribe(),
			);
	}

	onApply() {
		if (!this.form) throw Error('The form was never created');

		const value = this.form.getRawValue();
		this.lastValue.set(value);
		this.valid.set(this.determineValidity(value));

		this.filterChange.emit(value);
	}

	onRetry() {
		this.onApply();
	}

	onLoad() {
		this.loadChange.emit();
	}

	onClear() {
		this.clear.emit();
	}

	onReset() {
		if (!this.form) throw Error('The form was never created');
		this.form.markAsDirty();
		this.restoreDefaultValues();
	}

	onFinancialSelect(option: SelectSearchOption<CalculationType>) {
		const control = this.form?.get('financial');
		if (!control) return;

		control.markAsDirty();
		control.setValue(option);
	}
}
